/**
 * ContactSection Component
 * Displays contact information (phone, email, MC#, DOT#) with copy and action buttons
 */

import React, { useState, useCallback, useMemo } from 'react'
import { Box, Typography, IconButton, Tooltip } from '@mui/joy'
import { LAYOUT, TYPOGRAPHY } from '../../utils/constants'
import { CanonicalLoad } from '@numeo/types-core'
import { SectionHeader } from './SectionHeader'
import { CallIcon, EmailIcon } from '../../icons'
import { IconActionButton } from './IconActionButton'
import { TelegramIcon } from '../../icons/SourceIcons'
import { useRTSRating } from 'hooks/useRTSRating'
import CheckIcon from '@mui/icons-material/Check'
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded'
import { CopyIcon } from '../../icons'
import { getFormattedPhone } from '../../utils/phone'
import { initiateLoadHubCall } from '../../utils/callProvider'
import { useCallProvider } from '../../hooks/useCallProvider'
import { useExtensionInstalled } from 'hooks/useExtensionInstalled'

interface ContactSectionProps {
    load: CanonicalLoad
}

/** Save load with action type to localStorage for extension communication */
export const saveCurrentLoad = (load: CanonicalLoad, actionType: 'call' | 'email' = 'call') => {
    try {
        const loadWithAction = {
            ...load,
            actionType,
        }
        localStorage.setItem('currentLoad', JSON.stringify(loadWithAction))
    } catch (error) {
        console.error('Failed to save current load:', error)
    }
}

export const ContactSection: React.FC<ContactSectionProps> = ({ load }) => {
    const { ratingData } = useRTSRating(load.brokerMc || undefined)
    const [copiedPhone, setCopiedPhone] = useState(false)
    const [copiedEmail, setCopiedEmail] = useState(false)
    const [copiedMc, setCopiedMc] = useState(false)
    const [copiedDot, setCopiedDot] = useState(false)

    /** Memoize phone info (primary from load, fallback from RTS rating) */
    const phoneInfo = useMemo(() => {
        const primary = getFormattedPhone(load.brokerContactPhone)
        const fallback = getFormattedPhone(ratingData?.phone ?? null)
        return primary ?? fallback
    }, [load.brokerContactPhone, ratingData?.phone])

    /** Check if phone is available */
    const hasPhone = useMemo(() => Boolean(phoneInfo?.formatted), [phoneInfo])

    /** Memoize effective email (from load or RTS rating), split multiple emails */
    const emailList = useMemo(() => {
        const raw = load.brokerContactEmail || ratingData?.email || ''
        return raw
            .split(/[,;]\s*/)
            .map((e) => e.trim())
            .filter(Boolean)
    }, [load.brokerContactEmail, ratingData?.email])
    const effectiveEmail = emailList.join(', ')

    /** Check if email is available */
    const hasEmail = useMemo(() => emailList.length > 0, [emailList])

    const hasTelegramLink = Boolean(load.brokerTelegramAccountLink)

    /** Show membership warning only for TELEGRAM-sourced loads where we know the source chat. */
    const showTelegramSourceWarning = load.sourceType === 'TELEGRAM' && Boolean(load.sourceTelegramChatTitle) && Boolean(load.sourceTelegramChatType)

    /** Handle copy phone to clipboard */
    const handleCopyPhone = useCallback(async () => {
        if (!phoneInfo?.copyValue) return
        try {
            await navigator.clipboard.writeText(phoneInfo.copyValue)
            setCopiedPhone(true)
            setTimeout(() => setCopiedPhone(false), 2000)
        } catch (error) {
            console.error('Failed to copy phone:', error)
        }
    }, [phoneInfo?.copyValue])

    /** Handle copy email to clipboard */
    const handleCopyEmail = useCallback(async () => {
        if (!effectiveEmail) return
        try {
            await navigator.clipboard.writeText(effectiveEmail)
            setCopiedEmail(true)
            setTimeout(() => setCopiedEmail(false), 2000)
        } catch (error) {
            console.error('Failed to copy email:', error)
        }
    }, [effectiveEmail])

    /** Handle copy MC# to clipboard */
    const handleCopyMc = useCallback(async () => {
        if (!load.brokerMc) return
        try {
            await navigator.clipboard.writeText(load.brokerMc)
            setCopiedMc(true)
            setTimeout(() => setCopiedMc(false), 2000)
        } catch (error) {
            console.error('Failed to copy MC#:', error)
        }
    }, [load.brokerMc])

    /** Handle copy DOT# to clipboard */
    const handleCopyDot = useCallback(async () => {
        if (!load.brokerDot) return
        try {
            await navigator.clipboard.writeText(load.brokerDot)
            setCopiedDot(true)
            setTimeout(() => setCopiedDot(false), 2000)
        } catch (error) {
            console.error('Failed to copy DOT#:', error)
        }
    }, [load.brokerDot])

    const { provider: callProvider } = useCallProvider()
    const { confirmed: extensionConfirmed } = useExtensionInstalled()

    /** Memoize save handlers to prevent recreation */
    const handleSaveCall = useCallback(() => {
        saveCurrentLoad(load, 'call')
        // With the extension CONFIRMED installed (PING round-trip), its content script owns the
        // `.rc_call_icon` click and dials using the same (synced) provider — dialing here too would
        // double-call. Otherwise dial natively: the synchronous `installed` seed is true in any
        // Chrome, so gating on it would silently swallow the click when the extension is absent.
        if (!extensionConfirmed && phoneInfo?.formatted) {
            initiateLoadHubCall(callProvider, phoneInfo.formatted)
        }
    }, [load, extensionConfirmed, callProvider, phoneInfo])
    const handleSaveEmail = useCallback(() => saveCurrentLoad(load, 'email'), [load])

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: LAYOUT.GAP_8 }}>
            <SectionHeader title="CONTACT" />

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: LAYOUT.GAP_12 }}>
                {hasPhone ? (
                    <Box sx={{ display: 'flex', gap: LAYOUT.GAP_6, alignItems: 'center', minHeight: '18px' }}>
                        <Tooltip title={copiedPhone ? 'Copied!' : 'Copy phone'}>
                            <IconButton
                                size="sm"
                                variant="plain"
                                color="neutral"
                                onClick={handleCopyPhone}
                                sx={{
                                    p: '2px',
                                    minWidth: 'auto',
                                    flexShrink: 0,
                                    '&:hover': { bgcolor: 'rgba(0,0,0,0.05)' },
                                }}
                            >
                                {copiedPhone ? (
                                    <CheckIcon sx={{ width: '13px', height: '13px', color: 'success.main' }} />
                                ) : (
                                    <CopyIcon sx={{ width: '13px', height: '13px', color: 'var(--lh-accent)' }} />
                                )}
                            </IconButton>
                        </Tooltip>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', flex: 1, minWidth: 0 }}>
                            <Typography
                                level="body-sm"
                                fontWeight={500}
                                sx={{
                                    flex: 1,
                                    wordBreak: 'break-word',
                                    fontSize: TYPOGRAPHY.SIZE_11,
                                    lineHeight: TYPOGRAPHY.LINE_HEIGHT_14,
                                    color: 'text.primary',
                                }}
                            >
                                {phoneInfo?.formatted}
                            </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', gap: LAYOUT.GAP_3, flexShrink: 0 }}>
                            <IconActionButton icon={<CallIcon />} onClick={handleSaveCall} id={`${load.id}`} className="rc_call_icon" />
                        </Box>
                    </Box>
                ) : (
                    <Typography level="body-sm" fontWeight={400} sx={{ color: 'text.tertiary' }}>
                        Phone: N/A
                    </Typography>
                )}

                {hasEmail ? (
                    <Box sx={{ display: 'flex', gap: LAYOUT.GAP_6, alignItems: 'center', minHeight: '18px' }}>
                        <Tooltip title={copiedEmail ? 'Copied!' : 'Copy email'}>
                            <IconButton
                                size="sm"
                                variant="plain"
                                color="neutral"
                                onClick={handleCopyEmail}
                                sx={{
                                    p: '2px',
                                    minWidth: 'auto',
                                    flexShrink: 0,
                                    '&:hover': { bgcolor: 'rgba(0,0,0,0.05)' },
                                }}
                            >
                                {copiedEmail ? (
                                    <CheckIcon sx={{ width: '13px', height: '13px', color: 'success.main' }} />
                                ) : (
                                    <CopyIcon sx={{ width: '13px', height: '13px', color: 'var(--lh-accent)' }} />
                                )}
                            </IconButton>
                        </Tooltip>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px', flex: 1, minWidth: 0 }}>
                            {emailList.map((email, i) => (
                                <Typography
                                    key={i}
                                    level="body-sm"
                                    fontWeight={500}
                                    sx={{
                                        wordBreak: 'break-word',
                                        fontSize: TYPOGRAPHY.SIZE_11,
                                        lineHeight: TYPOGRAPHY.LINE_HEIGHT_14,
                                        color: 'text.primary',
                                    }}
                                >
                                    {email}
                                </Typography>
                            ))}
                        </Box>
                        <Box sx={{ display: 'flex', gap: LAYOUT.GAP_3, flexShrink: 0 }}>
                            <IconActionButton icon={<EmailIcon />} onClick={handleSaveEmail} id={load.id?.toString()} className="email_icon" />
                        </Box>
                    </Box>
                ) : !hasTelegramLink && !showTelegramSourceWarning ? (
                    <Typography level="body-sm" fontWeight={400} sx={{ color: 'text.tertiary', fontSize: TYPOGRAPHY.SIZE_11 }}>
                        Email: N/A
                    </Typography>
                ) : null}

                {showTelegramSourceWarning && (
                    <Box
                        role="note"
                        aria-label="Telegram chat membership required"
                        sx={{
                            display: 'flex',
                            gap: LAYOUT.GAP_6,
                            alignItems: 'flex-start',
                            p: '6px 8px',
                            borderRadius: '6px',
                            backgroundColor: 'rgba(240, 180, 0, 0.10)',
                            border: '1px solid rgba(240, 180, 0, 0.45)',
                        }}
                    >
                        <WarningAmberRoundedIcon sx={{ width: 14, height: 14, color: '#B58100', flexShrink: 0, mt: '1px' }} />
                        <Typography
                            level="body-sm"
                            sx={{ color: 'text.primary', fontSize: TYPOGRAPHY.SIZE_11, lineHeight: TYPOGRAPHY.LINE_HEIGHT_14, wordBreak: 'break-word' }}
                        >
                            To open this load, you need to be in the Telegram {load.sourceTelegramChatType}{' '}
                            <Typography component="span" fontWeight={600} sx={{ fontSize: TYPOGRAPHY.SIZE_11 }}>
                                &ldquo;{load.sourceTelegramChatTitle}&rdquo;
                            </Typography>
                            .
                        </Typography>
                    </Box>
                )}

                {hasTelegramLink && (
                    <Box sx={{ display: 'flex', gap: LAYOUT.GAP_6, alignItems: 'center', minHeight: '18px' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', flex: 1, minWidth: 0 }}>
                            <TelegramIcon sx={{ width: '13px', height: '13px', flexShrink: 0 }} />
                            <a
                                href={load.brokerTelegramAccountLink!}
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label="Contact on Telegram"
                                style={{
                                    fontSize: TYPOGRAPHY.SIZE_11,
                                    lineHeight: TYPOGRAPHY.LINE_HEIGHT_14,
                                    color: 'var(--lh-accent)',
                                    textDecoration: 'none',
                                    wordBreak: 'break-word',
                                    flex: 1,
                                }}
                            >
                                Contact on Telegram
                            </a>
                        </Box>
                    </Box>
                )}

                {load.brokerMc && (
                    <Box sx={{ display: 'flex', gap: LAYOUT.GAP_6, alignItems: 'center', minHeight: '18px' }}>
                        <Tooltip title={copiedMc ? 'Copied!' : 'Copy MC#'}>
                            <IconButton
                                size="sm"
                                variant="plain"
                                color="neutral"
                                onClick={handleCopyMc}
                                sx={{
                                    p: '2px',
                                    minWidth: 'auto',
                                    flexShrink: 0,
                                    '&:hover': { bgcolor: 'rgba(0,0,0,0.05)' },
                                }}
                            >
                                {copiedMc ? (
                                    <CheckIcon sx={{ width: '13px', height: '13px', color: 'success.main' }} />
                                ) : (
                                    <CopyIcon sx={{ width: '13px', height: '13px', color: 'var(--lh-accent)' }} />
                                )}
                            </IconButton>
                        </Tooltip>
                        <Typography
                            level="body-xs"
                            fontWeight={400}
                            sx={{
                                color: 'text.secondary',
                                flexShrink: 0,
                                fontSize: TYPOGRAPHY.SIZE_11,
                                lineHeight: TYPOGRAPHY.LINE_HEIGHT_14,
                            }}
                        >
                            MC#
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', flex: 1, minWidth: 0 }}>
                            <Typography
                                level="body-sm"
                                fontWeight={500}
                                sx={{
                                    flex: 1,
                                    wordBreak: 'break-word',
                                    fontSize: TYPOGRAPHY.SIZE_11,
                                    lineHeight: TYPOGRAPHY.LINE_HEIGHT_14,
                                    color: 'text.primary',
                                }}
                            >
                                {load.brokerMc}
                            </Typography>
                        </Box>
                    </Box>
                )}

                {load.brokerDot && (
                    <Box sx={{ display: 'flex', gap: LAYOUT.GAP_6, alignItems: 'center', minHeight: '18px' }}>
                        <Tooltip title={copiedDot ? 'Copied!' : 'Copy DOT#'}>
                            <IconButton
                                size="sm"
                                variant="plain"
                                color="neutral"
                                onClick={handleCopyDot}
                                sx={{
                                    p: '2px',
                                    minWidth: 'auto',
                                    flexShrink: 0,
                                    '&:hover': { bgcolor: 'rgba(0,0,0,0.05)' },
                                }}
                            >
                                {copiedDot ? (
                                    <CheckIcon sx={{ width: '13px', height: '13px', color: 'success.main' }} />
                                ) : (
                                    <CopyIcon sx={{ width: '13px', height: '13px', color: 'var(--lh-accent)' }} />
                                )}
                            </IconButton>
                        </Tooltip>
                        <Typography
                            level="body-xs"
                            fontWeight={400}
                            sx={{
                                color: 'text.secondary',
                                flexShrink: 0,
                                fontSize: TYPOGRAPHY.SIZE_11,
                                lineHeight: TYPOGRAPHY.LINE_HEIGHT_14,
                            }}
                        >
                            DOT#
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', flex: 1, minWidth: 0 }}>
                            <Typography
                                level="body-sm"
                                fontWeight={500}
                                sx={{
                                    flex: 1,
                                    wordBreak: 'break-word',
                                    fontSize: TYPOGRAPHY.SIZE_11,
                                    lineHeight: TYPOGRAPHY.LINE_HEIGHT_14,
                                    color: 'text.primary',
                                }}
                            >
                                {load.brokerDot}
                            </Typography>
                        </Box>
                    </Box>
                )}

                {!hasPhone && !hasEmail && !hasTelegramLink && !showTelegramSourceWarning && !load.brokerMc && !load.brokerDot && (
                    <Typography level="body-sm" fontWeight={400} sx={{ color: 'text.tertiary' }}>
                        No contact information available
                    </Typography>
                )}
            </Box>
        </Box>
    )
}
